import React from 'react'
import { Loader2, Check, AlertCircle } from 'lucide-react'

type ButtonState = 'idle' | 'loading' | 'success' | 'error' | 'disabled'
type ButtonVariant = 'primary' | 'secondary' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface LoadingButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  state?: ButtonState
  loading?: boolean
  isLoading?: boolean
  variant?: ButtonVariant
  size?: ButtonSize
  children: React.ReactNode
  loadingText?: string
  successText?: string
  errorText?: string
  showIcon?: boolean
}

const stateConfig = {
  idle: {
    text: 'default',
    icon: null,
    disabled: false,
  },
  loading: {
    text: 'muted',
    icon: 'spinner',
    disabled: true,
  },
  success: {
    text: 'default',
    icon: 'check',
    disabled: true,
  },
  error: {
    text: 'error',
    icon: 'error',
    disabled: false,
  },
  disabled: {
    text: 'muted',
    icon: null,
    disabled: true,
  },
}

const variantStyles = {
  primary:
    'bg-[#6D3A0D] text-white hover:bg-[#5D2A0D] disabled:bg-gray-400 disabled:text-gray-200',
  secondary:
    'border-2 border-[#6D3A0D] text-[#6D3A0D] hover:bg-[#F6EEE7] disabled:border-gray-300 disabled:text-gray-400',
  danger:
    'bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400 disabled:text-gray-200',
}

const sizeStyles = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
}

export default function LoadingButton({
  state: inputState = 'idle',
  loading,
  isLoading,
  variant = 'primary',
  size = 'md',
  children,
  loadingText = 'Loading...',
  successText = 'Success!',
  errorText = 'Error',
  showIcon = true,
  disabled = false,
  className = '',
  ...props
}: LoadingButtonProps) {
  const currentState: ButtonState = (loading || isLoading) ? 'loading' : inputState
  const isDisabled = disabled || currentState === 'loading' || currentState === 'disabled'
  const config = stateConfig[currentState]

  const getButtonText = () => {
    switch (currentState) {
      case 'loading':
        return loadingText
      case 'success':
        return successText
      case 'error':
        return errorText
      default:
        return children
    }
  }

  const getIcon = () => {
    if (!showIcon) return null

    switch (config.icon) {
      case 'spinner':
        return <Loader2 className="w-4 h-4 animate-spin" />
      case 'check':
        return <Check className="w-4 h-4" />
      case 'error':
        return <AlertCircle className="w-4 h-4" />
      default:
        return null
    }
  }

  return (
    <button
      {...props}
      disabled={isDisabled}
      className={`
        inline-flex items-center justify-center gap-2 font-semibold rounded-lg
        transition-all duration-200 ease-in-out
        ${sizeStyles[size]}
        ${variantStyles[variant]}
        ${currentState === 'success' ? 'ring-2 ring-green-500 ring-opacity-50' : ''}
        ${currentState === 'error' ? 'ring-2 ring-red-500 ring-opacity-50' : ''}
        ${className}
      `}
    >
      {getIcon()}
      <span>{getButtonText()}</span>
    </button>
  )
}
